import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useStyles } from './EditImage.styles';
import { Box, Button } from '@mui/material';
import { Cropper } from 'react-cropper';
import _ from 'lodash';

import 'cropperjs/dist/cropper.css';
import { Redo, Undo, ZoomIn, ZoomOut } from '@mui/icons-material';

const EditImage = ({ file, setFile, width, height, actionCallback }) => {
  const cropperRef = useRef(null);

  const handleCropChange = useCallback(() => {
    try {
      const croppedCanvas = cropperRef?.current?.cropper?.getCroppedCanvas({
        width,
        height,
      });

      if (croppedCanvas) {
        croppedCanvas.toBlob((blob) => {
          if (!blob) return;
          blob.name = file.id;
          blob.id = file.id;
          setFile(blob);
        });
      }
      actionCallback({ type: 'crop', status: 'success', payload: {} });
    } catch (err) {
      console.error('EditImage - handleCropChange error:', err);
      actionCallback({ type: 'crop', status: 'error', payload: err });
    }
  }, [actionCallback, file.id, height, setFile, width]);

  const onCrop = _.debounce(handleCropChange, 300);

  const handleRotate = useCallback(
    (degree) => {
      cropperRef?.current?.cropper?.rotate(degree);
      handleCropChange();
    },
    [cropperRef, handleCropChange]
  );

  const handleZoom = useCallback(
    (ratio) => {
      cropperRef?.current?.cropper?.zoom(ratio);
      handleCropChange();
    },
    [cropperRef, handleCropChange]
  );

  return (
    <Box sx={useStyles.container}>
      <Cropper
        src={file.url}
        style={useStyles.cropper}
        aspectRatio={width / height}
        guides={false}
        checkCrossOrigin={false}
        crop={onCrop}
        ref={cropperRef}
      />
      <Box sx={useStyles.actions}>
        <Button
          data-testid="rotate-left"
          onClick={() => handleRotate(-90)}
          variant="outlined"
          sx={useStyles.button}
        >
          <Undo />
        </Button>
        <Button
          data-testid="rotate-right"
          onClick={() => handleRotate(90)}
          variant="outlined"
          sx={useStyles.button}
        >
          <Redo />
        </Button>
        <Button
          data-testid="zoom-out"
          onClick={() => handleZoom(-0.1)}
          variant="outlined"
          sx={useStyles.button}
        >
          <ZoomOut />
        </Button>
        <Button
          data-testid="zoom-in"
          onClick={() => handleZoom(0.1)}
          variant="outlined"
          sx={useStyles.button}
        >
          <ZoomIn />
        </Button>
      </Box>
    </Box>
  );
};

EditImage.propTypes = {
  file: PropTypes.shape({
    url: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    path: PropTypes.string,
    mime: PropTypes.string,
  }).isRequired,
  setFile: PropTypes.func.isRequired,
  actionCallback: PropTypes.func,
  height: PropTypes.number,
  width: PropTypes.number,
};

EditImage.defaultProps = {
  height: 330,
  width: 630,
};

export default EditImage;
